/**
 * AssigneePicker — choose who an Action Plan play is assigned to (SYR-68).
 *
 * A compact, searchable picker over the organisation's users (plus an
 * "Unassigned" option). Defaults to the play's creator; clicking it lets the
 * marketer reassign the play to anyone in the org.
 */

import React, { useMemo } from 'react';
import { Select } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { THEME } from '../config/theme';

export interface OrgUser {
  id: number;
  firstName?: string;
  lastName?: string;
  email: string;
}

/** Best display name for a user — full name, else email. */
export function userDisplayName(u?: OrgUser): string {
  if (!u) return '';
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || u.email || `User ${u.id}`;
}

/** Sentinel select value for "Unassigned" — no real user has id 0. */
const UNASSIGNED = 0;

export interface AssigneePickerProps {
  users: OrgUser[];
  value: number | null;
  /** Server-resolved name for the current assignee (covers users not in the
   *  pickable org list, e.g. a platform-admin or cross-org creator). */
  valueLabel?: string | null;
  onChange: (userId: number | null) => void;
  loading?: boolean;
}

export const AssigneePicker: React.FunctionComponent<AssigneePickerProps> = ({
  users,
  value,
  valueLabel,
  onChange,
  loading,
}) => {
  const intl = useIntl();
  const options = useMemo(() => {
    const list = [
      {
        value: UNASSIGNED,
        label: intl.formatMessage({
          id: 'marketerView.actionPlan.unassigned',
          defaultMessage: 'Unassigned',
        }),
      },
      ...users.map((u) => ({ value: u.id, label: userDisplayName(u) })),
    ];
    // Assignee not in the pickable org list (e.g. a platform-admin or
    // cross-org creator) — show their server-resolved name, not a bare id.
    if (value != null && !users.some((u) => u.id === value)) {
      list.push({
        value,
        label:
          valueLabel?.trim() ||
          intl.formatMessage(
            { id: 'marketerView.actionPlan.userN', defaultMessage: 'User {id}' },
            { id: value },
          ),
      });
    }
    return list;
  }, [users, value, valueLabel, intl]);

  return (
    <Select
      size="small"
      showSearch
      value={value ?? UNASSIGNED}
      loading={loading}
      onChange={(v) => onChange(v === UNASSIGNED ? null : (v as number))}
      options={options}
      optionFilterProp="label"
      suffixIcon={<UserOutlined style={{ color: THEME.textMuted }} />}
      onClick={(e) => e.stopPropagation()}
      style={{ minWidth: 158 }}
      title={intl.formatMessage({
        id: 'marketerView.actionPlan.assignTo',
        defaultMessage: 'Assign to',
      })}
    />
  );
};
